<script setup lang="ts">
import { ZhDecisionCard } from '@zerohuman/ui'
import type { CeoQuestionAnswer, GateDecision, GateRow } from '@/api'
import CardCloseSpace from '@/components/CardCloseSpace.vue'
import GateDecisionActions from '@/components/GateDecisionActions.vue'
import { useUiMode } from '@/composables/useUiMode'
import { gateKind } from '@/lib/gateKind'
import { toolLabel } from '@/lib/presentation'

/**
 * A waiting gate as the run page asks about it, in its timeline step or on its own: the inline decision card
 * (ZhDecisionCard, as Gates and chat draw it) holding the form that decides it (GateDecisionActions). The
 * `payload` slot passes through to the form. Attributes land on the card.
 *
 * Once decided (`decided`, from useGateDecisions) the card shows the decision and fades as the Gates cards
 * do, then its space closes up (CardCloseSpace, shared with the chat tool approval card) so what sits below
 * slides up: keep it on the page for MOTION.decisionMs plus GATE_CARD_CLOSE_MS.
 *
 * The card says what kind of decision it is (#1760, `gateKind`): "Approve an action" or "Answer a question", in
 * place of the plain kicker, with the kind's own edge.
 */
defineOptions({ inheritAttrs: false })
defineProps<{
    gate: GateRow
    busy: boolean
    decided?: GateDecision
    /** The choice it was decided through, in its button's words, when the gate offered choices. */
    decidedLabel?: string
}>()
const emit = defineEmits<{
    decide: [
        id: string,
        decision: GateDecision,
        note: string,
        answers?: CeoQuestionAnswer[],
        /** The payload as the CEO edited it before approving (os#911); absent when they changed nothing. */
        edit?: Record<string, unknown>,
        /** The key of the choice the decision was made through, on a gate that offers choices. */
        choice?: string,
    ]
}>()

/** #1749: the card is titled by the tool's vendor in Standard and by the raw tool name in Advanced. */
const { mode: uiMode } = useUiMode()
</script>

<template>
	<CardCloseSpace :decided="decided">
		<ZhDecisionCard
			v-bind="$attrs"
			variant="inline"
			kicker="Gate"
			:kind="gateKind(gate)"
			:decision="{ title: toolLabel(gate.toolName, uiMode) }"
			:decided="decided"
			:decided-label="decidedLabel"
		>
			<template #actions>
				<div class="w-full space-y-3 text-sm">
					<GateDecisionActions
						:gate="gate"
						:busy="busy || decided !== undefined"
						@decide="
							(id, decision, note, answers, edit, choice) =>
								emit('decide', id, decision, note, answers, edit, choice)
						"
					>
						<template v-if="$slots.payload" #payload><slot name="payload" /></template>
					</GateDecisionActions>
				</div>
			</template>
		</ZhDecisionCard>
	</CardCloseSpace>
</template>
